import { useBreadcrumbs } from '@features/layout/hooks/useBreadcrumbs';
import { Alert, Spin } from 'antd';
import { FC, ReactNode, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';

import { MarketplaceStepFooter } from '../components/MarketplaceStepFooter';
import { MarketplaceStepHeader } from '../components/MarketplaceStepHeader';
import { ConfigurationStep } from '../components/steps/ConfigurationStep';
import { CreationStep } from '../components/steps/CreationStep';
import { LandingPageStep } from '../components/steps/LandingPageStep';
import { ScheduleStep } from '../components/steps/ScheduleStep';
import { useMarketplaceForm } from '../hooks/useMarketplaceForm';

const TOTAL_STEPS = 4;

export const CreateOrUpdateMarketplaceScreen: FC = () => {
  const {
    isEditing,
    headerInfo,
    marketplaceFormMethods,
    marketplaceId,
    handleSaveMarketplace,
    handleBack,
    isLoadingSubmit,
    isLoadingProduct,
    isPublished,
    isPublishActionDisabled,
    currentStep,
    setCurrentStep,
    stepsCompleted,
    stepsWithErrors,
    publishActionLabel,
    showUnsavedAlert,
  } = useMarketplaceForm();

  useBreadcrumbs([
    {
      label: 'Marketplace',
      route: '/marketplace',
    },
    {
      label: headerInfo.title,
    },
  ]);

  const renderStep = useMemo(() => {
    const steps: Record<number, ReactNode> = {
      0: <CreationStep />,
      1: <ConfigurationStep />,
      2: <LandingPageStep />,
      3: <ScheduleStep />,
    };
    return steps[currentStep] || null;
  }, [currentStep, marketplaceId]);

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep(currentStep + 1);
      if (!isPublished) {
        handleSaveMarketplace();
      }
    }
  };

  if (isLoadingProduct) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <FormProvider {...marketplaceFormMethods}>
      <MarketplaceStepHeader
        currentStep={currentStep}
        onChangeStep={setCurrentStep}
        stepsCompleted={stepsCompleted}
        stepsWithErrors={stepsWithErrors}
        title={headerInfo.title}
        subtitle={headerInfo.subTitle}
        backToPath={handleBack}
      />
      {isPublished && (
        <Alert
          type='info'
          message='Este produto está publicado. Alterações passarão por validações mais restritivas para preservar a disponibilidade na plataforma.'
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {showUnsavedAlert && (
        <Alert
          type='info'
          message='Você tem alterações não salvas. Clique em "Salvar Rascunho" para não perder o progresso.'
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}
      <div style={{ paddingBottom: 72, position: 'relative' }}>
        {renderStep}
        <MarketplaceStepFooter
          isEditing={isEditing}
          isPublished={isPublished}
          currentStep={currentStep}
          onSaveDraft={async () => {
            await handleSaveMarketplace();
          }}
          onPublishChanges={async () => {
            await handleSaveMarketplace('publish');
          }}
          onNext={handleNext}
          publishActionLabel={publishActionLabel}
          isPrimaryActionDisabled={isPublishActionDisabled}
          isLoading={isLoadingSubmit}
        />
      </div>
    </FormProvider>
  );
};
